import { DateTime } from 'luxon';
import {
    ConfirmationHeader,
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
} from './Slidein.tsx';
import { Option, SlideInSubheader } from './SlideInSubheader.tsx';
import { ButtonSpecialState } from '../buttons/IconButton.tsx';
import { Translation, Translations } from '../../common/i18n/Text.ts';
import {
    getOrElse,
    isDefined,
    isUndefined,
    Maybe,
    thenElse,
} from 'dg-web-shared/lib/MaybeV2.ts';
import { DatePicker, DateSelectHandler } from '../date-picker/DatePicker.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DivGenerator } from 'dg-web-shared/lib/ReactHelpers.tsx';
import * as Logger from 'dg-web-shared/lib/Logger.ts';
import { css } from '@emotion/css';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import React, { type JSX } from 'react';

const texts: Translations<DatePickerSlideInTexts> = {
    de: {
        ModeDefined: (): string => 'Definiert',
        ModeMaximum: (): string => 'Maximale Dauer',
    },
    it: {
        ModeDefined: (): string => 'Definita',
        ModeMaximum: (): string => 'Durata massima',
    },
    fr: {
        ModeDefined: (): string => 'Définie',
        ModeMaximum: (): string => 'Durée maximale',
    },
};

export interface DatePickerSlideInTexts {
    ModeDefined: Translation;
    ModeMaximum: Translation;
}

interface DatePickerSlideInProps {
    open: boolean;
    caption: string;
    outsideBody: boolean;
    selectedDate: Maybe<DateTime>;
    onSelect: DateSelectHandler;
    onClose: () => void;
    minDate?: Maybe<DateTime>;
    maxDate?: Maybe<DateTime>;
    canSelectMaxDate?: boolean;
    language: string;
}

enum DateSelectionMethod {
    DEFINED,
    MAXIMUM,
}

const containerStyle = css({
    width: 280,
    marginLeft: 'auto',
    marginRight: 'auto',
});

export const SlideInDatePickerContainer = DivGenerator(containerStyle);

interface DatePickerSlideInState {
    selectionMethod: DateSelectionMethod;
    selectedDate: Maybe<DateTime>;
}

function getInitialState(
    props: DatePickerSlideInProps,
): DatePickerSlideInState {
    const selectionMethod: DateSelectionMethod =
        props.canSelectMaxDate &&
        isDefined(props.selectedDate) &&
        isDefined(props.maxDate) &&
        props.selectedDate.toMillis() === props.maxDate.toMillis()
            ? DateSelectionMethod.MAXIMUM
            : DateSelectionMethod.DEFINED;
    return {
        selectionMethod,
        selectedDate: props.selectedDate,
    };
}

export function DatePickerSlideIn(props: DatePickerSlideInProps) {
    const [state, setState] = React.useState<DatePickerSlideInState>(() =>
        getInitialState(props),
    );

    // Was UNSAFE_componentWillReceiveProps: reset the selection whenever the
    // slide-in opens or closes. React's documented equivalent is to adjust the
    // state during render when the prop it keys off has changed.
    const [previousOpen, setPreviousOpen] = React.useState(props.open);
    if (previousOpen !== props.open) {
        setPreviousOpen(props.open);
        setState(getInitialState(props));
    }

    function txt(): DatePickerSlideInTexts {
        return texts[props.language];
    }

    function onChangeSelectionMethod(
        selectedMethod: DateSelectionMethod,
    ): void {
        if (selectedMethod === DateSelectionMethod.MAXIMUM) {
            if (isUndefined(props.maxDate)) {
                Logger.warning(
                    'required prop maxDate is missing with canSelectMaxDate',
                );
                return;
            }
            const maxDate: DateTime = props.maxDate;
            setState({
                selectionMethod: selectedMethod,
                selectedDate: maxDate,
            });
            props.onSelect(maxDate);
        } else {
            setState({
                selectedDate: null,
                selectionMethod: selectedMethod,
            });
        }
    }

    const selectionMethodOptions: Option<DateSelectionMethod>[] = [
        {
            id: DateSelectionMethod.DEFINED,
            displayText: txt().ModeDefined(),
        },
        {
            id: DateSelectionMethod.MAXIMUM,
            displayText: txt().ModeMaximum(),
        },
    ];
    const showSubheader: boolean = getOrElse<boolean>(
        props.canSelectMaxDate,
        false,
    );
    const hidePicker = state.selectionMethod === DateSelectionMethod.MAXIMUM;

    return (
        <HalfSlideIn open={props.open} outsideBody={props.outsideBody}>
            <SecondLevelHeader onClose={props.onClose}>
                <SlideInHeaderTexts title={props.caption} hasLeftIcon={false} />
            </SecondLevelHeader>
            {showSubheader && (
                <SlideInSubheader
                    options={selectionMethodOptions}
                    value={state.selectionMethod}
                    onSelect={method => onChangeSelectionMethod(method)}
                />
            )}
            <SlideInBody hasSubheader={showSubheader}>
                <SlideInDatePickerContainer>
                    <SelectedDateDisplay
                        selectedDate={props.selectedDate}
                        caption={props.caption}
                    />
                    {!hidePicker && (
                        <DatePicker
                            selectedDate={state.selectedDate}
                            currentDate={getOrElse(
                                props.minDate,
                                DateTime.now(),
                            )}
                            onSelect={props.onSelect}
                            minDate={props.minDate}
                            maxDate={props.maxDate}
                            language={props.language}
                        />
                    )}
                </SlideInDatePickerContainer>
            </SlideInBody>
        </HalfSlideIn>
    );
}

interface ConfirmableProps {
    open: boolean;
    title: string;
    outsideBody: boolean;
    defaultValue: Maybe<DateTime>;
    minDate?: Maybe<DateTime>;
    maxDate?: Maybe<DateTime>;
    onCancel: () => void;
    onConfirm: (selectedDate: DateTime) => void;
    language: string;
}

export function ConfirmableDatePickerSlideIn(props: ConfirmableProps) {
    const [ownSelectedDate, setOwnSelectedDate] =
        React.useState<Maybe<DateTime>>(null);

    const selectedDate = getOrElse(ownSelectedDate, props.defaultValue);
    return (
        <HalfSlideIn open={props.open} outsideBody={props.outsideBody}>
            <ConfirmationHeader
                language={props.language}
                title={props.title}
                onCancel={() => {
                    setOwnSelectedDate(null);
                    props.onCancel();
                }}
                confirmButtonSpecialState={
                    isUndefined(selectedDate)
                        ? ButtonSpecialState.DISABLED
                        : null
                }
                onConfirm={() => {
                    if (isDefined(selectedDate)) {
                        setOwnSelectedDate(null);
                        props.onConfirm(selectedDate);
                    }
                }}
            />
            <SlideInBody>
                <div className={containerStyle}>
                    <DatePicker
                        selectedDate={selectedDate}
                        currentDate={DateTime.now()}
                        onSelect={(v: DateTime) => setOwnSelectedDate(v)}
                        minDate={props.minDate}
                        maxDate={props.maxDate}
                        language={props.language}
                    />
                </div>
            </SlideInBody>
        </HalfSlideIn>
    );
}

interface SelectedDateDisplayProps {
    selectedDate: Maybe<DateTime>;
    caption: string;
}

function SelectedDateDisplay(props: SelectedDateDisplayProps): JSX.Element {
    const dateString = thenElse(
        props.selectedDate,
        d => Formatter.dayMonthYear(d),
        '',
    );
    return (
        <div
            className={css({
                ...Typo.captionC1,
                marginBottom: 20,
            })}
        >
            {props.caption}
            <span
                className={css({
                    ...Typo.captionC2,
                    marginLeft: 5,
                })}
            >
                {dateString}
            </span>
        </div>
    );
}
